import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Cropper from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import { FaUserCircle, FaTrashAlt, FaExclamationTriangle, FaShieldAlt, FaLock, FaCamera, FaImage, FaEnvelope, FaKey, FaCheck, FaTimes } from 'react-icons/fa';
import { showSuccess, showError } from '../utils/toast';

const Settings = () => {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // Sayfa yüklendiğinde en üste çık
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  
  // Crop state
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  
  // Email değiştirme state
  const [newEmail, setNewEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
  
  // Şifre değiştirme state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  // Resim seçildiğinde crop modal'ı aç
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Dosya boyutu kontrolü (max 5MB - crop öncesi)
    if (file.size > 5 * 1024 * 1024) {
      showError('Dosya boyutu en fazla 5MB olabilir');
      return;
    }

    // Dosya tipi kontrolü
    if (!file.type.startsWith('image/')) {
      showError('Lütfen geçerli bir resim dosyası seçin');
      return;
    }

    // Dosyayı oku ve crop modal'ı aç
    const reader = new FileReader();
    reader.onloadend = () => {
      setImageSrc(reader.result);
      setShowCropModal(true);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    };
    reader.readAsDataURL(file);
  };

  // Crop tamamlandığında çağrılır
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // getCroppedImg helper function (react-easy-crop önerisi)
  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      // Base64 data URL için crossOrigin gerekmez
      image.src = url;
    });

  const getCroppedImg = async (imageSrc, pixelCrop) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('Canvas context bulunamadı');
    }

    // Canvas boyutunu crop alanına göre ayarla
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    // Kırpılan alanı çiz
    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    // Base64'e çevir (JPEG, %90 kalite)
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Canvas boş'));
            return;
          }
          const reader = new FileReader();
          reader.addEventListener('load', () => resolve(reader.result));
          reader.addEventListener('error', (error) => reject(error));
          reader.readAsDataURL(blob);
        },
        'image/jpeg',
        0.9
      );
    });
  };

  // Kırpılan resmi oluştur ve yükle
  const createCroppedImage = useCallback(async () => {
    if (!imageSrc || !croppedAreaPixels) {
      showError('Lütfen önce fotoğrafı kırpın');
      return;
    }

    setIsUploadingImage(true);

    try {
      console.log('Crop başlatılıyor...', { croppedAreaPixels });
      
      // Kırpılan resmi al
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);
      
      console.log('Crop tamamlandı, base64 uzunluğu:', croppedImage.length);

      if (!croppedImage) {
        throw new Error('Kırpılan resim oluşturulamadı');
      }

      // API'ye gönder
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      if (!token) {
        throw new Error('Oturum bulunamadı. Lütfen tekrar giriş yapın.');
      }

      // API URL'i oluştur (axios baseURL kullan)
      const endpoint = '/api/auth/update-profile-image';
      
      console.log('API çağrısı yapılıyor...', { 
        endpoint,
        baseURL: axios.defaults.baseURL || 'boş (relative path kullanılacak)',
        fullURL: `${axios.defaults.baseURL || ''}${endpoint}`,
        tokenExists: !!token 
      });
      
      const response = await axios.put(endpoint, 
        { profileImage: croppedImage },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('API yanıtı:', response.data);

      if (response.data.user) {
        // Storage'ı güncelle
        const storage = localStorage.getItem('token') ? localStorage : sessionStorage;
        storage.setItem('user', JSON.stringify(response.data.user));
        
        // Context'i güncelle
        setUser(response.data.user);
        showSuccess('Profil resmi başarıyla güncellendi');
        
        // Crop modal'ı kapat
        setShowCropModal(false);
        setImageSrc(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedAreaPixels(null);
        
        // File input'u sıfırla
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        throw new Error('Kullanıcı bilgisi alınamadı');
      }
      setIsUploadingImage(false);
    } catch (error) {
      console.error('Resim yükleme hatası - Detay:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      console.error('Error status:', error.response?.status);
      console.error('Request URL:', error.config?.url);
      
      let errorMessage = 'Resim yüklenirken bir hata oluştu';
      
      if (error.response?.status === 404) {
        errorMessage = 'API endpoint bulunamadı (404). Backend güncel mi kontrol edin.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showError(errorMessage);
      setIsUploadingImage(false);
    }
  }, [imageSrc, croppedAreaPixels, setUser]);

  const handleRemoveImage = async () => {
    setIsUploadingImage(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      if (!token) {
        throw new Error('Oturum bulunamadı. Lütfen tekrar giriş yapın.');
      }

      // API endpoint
      const endpoint = '/api/auth/update-profile-image';
      
      const response = await axios.put(endpoint, 
        { profileImage: null },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.user) {
        // Storage'ı güncelle
        const storage = localStorage.getItem('token') ? localStorage : sessionStorage;
        storage.setItem('user', JSON.stringify(response.data.user));
        
        // Context'i güncelle
        setUser(response.data.user);
        showSuccess('Profil resmi kaldırıldı');
      }
      setIsUploadingImage(false);
    } catch (error) {
      console.error('Resim kaldırma hatası:', error);
      showError('Resim kaldırılırken bir hata oluştu');
      setIsUploadingImage(false);
    }
  };

  const handleUpdateEmail = async (e) => {
    e.preventDefault();

    if (!newEmail.trim()) {
      showError('Lütfen yeni email adresinizi girin');
      return;
    }

    setIsUpdatingEmail(true);

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.put('/api/auth/update-email', 
        { 
          newEmail: newEmail.trim(),
          verificationCode: emailCodeSent ? emailCode : undefined
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.codeSent) {
        setEmailCodeSent(true);
        showSuccess('Doğrulama kodu yeni email adresinize gönderildi');
      } else if (response.data.user) {
        // Email güncellendi
        const storage = localStorage.getItem('token') ? localStorage : sessionStorage;
        storage.setItem('user', JSON.stringify(response.data.user));
        setUser(response.data.user);
        setNewEmail('');
        setEmailCode('');
        setEmailCodeSent(false);
        setShowEmailModal(false);
        showSuccess('Email adresi başarıyla güncellendi');
      }
      setIsUpdatingEmail(false);
    } catch (error) {
      console.error('Email güncelleme hatası:', error);
      showError(error.response?.data?.message || 'Email güncellenirken bir hata oluştu');
      setIsUpdatingEmail(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();

    if (!currentPassword.trim()) {
      showError('Lütfen mevcut şifrenizi girin');
      return;
    }

    if (!newPassword.trim() || newPassword.length < 6) {
      showError('Yeni şifre en az 6 karakter olmalıdır');
      return;
    }

    if (newPassword !== confirmPassword) {
      showError('Yeni şifreler eşleşmiyor');
      return;
    }

    setIsUpdatingPassword(true);

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.put('/api/auth/update-password', 
        { 
          currentPassword,
          newPassword
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.message) {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setShowPasswordModal(false);
        showSuccess('Şifre başarıyla güncellendi');
      }
      setIsUpdatingPassword(false);
    } catch (error) {
      console.error('Şifre güncelleme hatası:', error);
      showError(error.response?.data?.message || 'Şifre güncellenirken bir hata oluştu');
      setIsUpdatingPassword(false);
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();

    // Onay metni kontrolü
    if (confirmText !== 'HESABIMI SIL') {
      showError('Lütfen "HESABIMI SIL" yazarak onaylayın');
      return;
    }

    if (!password.trim()) {
      showError('Lütfen şifrenizi girin');
      return;
    }

    setIsDeleting(true);

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.delete('/api/auth/delete-account', {
        headers: {
          Authorization: `Bearer ${token}`
        },
        data: { password }
      });

      if (response.data.success) {
        showSuccess('Hesabınız kalıcı olarak silindi');
        
        // 1.5 saniye sonra logout ve ana sayfaya yönlendir
        setTimeout(() => {
          logout();
          navigate('/');
        }, 1500);
      }
    } catch (error) {
      console.error('Hesap silme hatası:', error);
      showError(error.response?.data?.message || 'Hesap silinirken bir hata oluştu');
      setIsDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
            <FaUserCircle className="text-white text-3xl" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
            Hesap Ayarları
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Hesap bilgilerinizi ve tercihlerinizi yönetin
          </p>
        </div>

        {/* User Info Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 mb-6">
          {/* Profile Image Section */}
          <div className="flex flex-col items-center mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
            <div className="relative group">
              {user?.profileImage ? (
                <img 
                  src={user.profileImage} 
                  alt={user.name}
                  className="w-32 h-32 rounded-full object-cover border-4 border-blue-500"
                />
              ) : (
                <div className="w-32 h-32 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center border-4 border-blue-500">
                  <span className="text-white text-5xl font-bold">
                    {user?.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              
              {/* Upload Button Overlay */}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingImage}
                className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
              >
                {isUploadingImage ? (
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                ) : (
                  <FaCamera className="text-white text-2xl" />
                )}
              </button>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
                key={user?.profileImage} // Force re-render
              />
            </div>

            {/* Image Actions */}
            <div className="flex space-x-3 mt-4">
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingImage}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg transition-colors text-sm font-medium"
              >
                <FaImage />
                <span>{user?.profileImage ? 'Değiştir' : 'Fotoğraf Ekle'}</span>
              </button>
              
              {user?.profileImage && (
                <button
                  onClick={handleRemoveImage}
                  disabled={isUploadingImage}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-lg transition-colors text-sm font-medium"
                >
                  <FaTrashAlt />
                  <span>Kaldır</span>
                </button>
              )}
            </div>
          </div>

          {/* User Details */}
          <div className="flex items-center space-x-4 mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {user?.name}
              </h2>
              <p className="text-gray-600 dark:text-gray-400">{user?.email}</p>
              <div className="flex items-center space-x-2 mt-1">
                {user?.isVerified ? (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                    <FaShieldAlt className="mr-1" /> Doğrulanmış
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                    <FaExclamationTriangle className="mr-1" /> Doğrulanmamış
                  </span>
                )}
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {user?.role === 'admin' ? 'Admin' : 'Kullanıcı'}
                </span>
              </div>
            </div>
          </div>

          {/* Account Details */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Kayıt Tarihi
              </label>
              <p className="text-gray-900 dark:text-white">
                {user?.createdAt ? (
                  new Date(user.createdAt).toLocaleDateString('tr-TR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })
                ) : (
                  'Tarih bilgisi mevcut değil'
                )}
              </p>
            </div>
            {user?.lastLogin && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Son Giriş
                </label>
                <p className="text-gray-900 dark:text-white">
                  {new Date(user.lastLogin).toLocaleString('tr-TR')}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 mb-6">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            Hesap Ayarları
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => setShowEmailModal(true)}
              className="flex items-center space-x-3 px-6 py-4 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-colors border border-blue-200 dark:border-blue-800"
            >
              <FaEnvelope className="text-blue-600 dark:text-blue-400 text-2xl" />
              <div className="text-left">
                <p className="font-semibold text-gray-900 dark:text-white">Email Değiştir</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Email adresinizi güncelleyin</p>
              </div>
            </button>

            <button
              onClick={() => setShowPasswordModal(true)}
              className="flex items-center space-x-3 px-6 py-4 bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded-lg transition-colors border border-purple-200 dark:border-purple-800"
            >
              <FaKey className="text-purple-600 dark:text-purple-400 text-2xl" />
              <div className="text-left">
                <p className="font-semibold text-gray-900 dark:text-white">Şifre Değiştir</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Hesap şifrenizi güncelleyin</p>
              </div>
            </button>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800 rounded-2xl shadow-xl p-8">
          <div className="flex items-center space-x-3 mb-4">
            <FaExclamationTriangle className="text-red-600 dark:text-red-400 text-2xl" />
            <h3 className="text-2xl font-bold text-red-900 dark:text-red-400">
              Tehlikeli Bölge
            </h3>
          </div>
          
          <p className="text-red-800 dark:text-red-300 mb-6 leading-relaxed">
            Hesabınızı silmek kalıcıdır ve <strong>geri alınamaz</strong>. Tüm portföy verileriniz, 
            işlem geçmişiniz ve kişisel bilgileriniz tamamen silinecektir.
          </p>

          <button
            onClick={() => setShowDeleteModal(true)}
            className="flex items-center space-x-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-all shadow-md hover:shadow-lg"
          >
            <FaTrashAlt />
            <span>Hesabımı Kalıcı Olarak Sil</span>
          </button>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-8 animate-fade-in">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
                  <FaExclamationTriangle className="text-red-600 dark:text-red-400 text-3xl" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  Hesabınızı Silmek İstediğinizden Emin misiniz?
                </h2>
                <p className="text-red-600 dark:text-red-400 font-semibold">
                  Bu işlem geri alınamaz!
                </p>
              </div>

              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
                <p className="text-sm text-red-800 dark:text-red-300 mb-2">
                  <strong>Silinecek veriler:</strong>
                </p>
                <ul className="text-sm text-red-700 dark:text-red-400 space-y-1 list-disc list-inside">
                  <li>Tüm portföy verileriniz</li>
                  <li>İşlem geçmişiniz</li>
                  <li>Hesap bilgileriniz</li>
                  <li>Kişisel verileriniz</li>
                </ul>
              </div>

              <form onSubmit={handleDeleteAccount} className="space-y-4">
                {/* Onay Metni */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Devam etmek için <strong>"HESABIMI SIL"</strong> yazın:
                  </label>
                  <input
                    type="text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="HESABIMI SIL"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    disabled={isDeleting}
                  />
                </div>

                {/* Şifre */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <FaLock className="inline mr-1" />
                    Şifrenizi girin:
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Şifreniz"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    disabled={isDeleting}
                  />
                </div>

                {/* Buttons */}
                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowDeleteModal(false);
                      setPassword('');
                      setConfirmText('');
                    }}
                    disabled={isDeleting}
                    className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-white font-semibold rounded-lg transition-colors"
                  >
                    Vazgeç
                  </button>
                  <button
                    type="submit"
                    disabled={isDeleting || confirmText !== 'HESABIMI SIL' || !password}
                    className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
                  >
                    {isDeleting ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Siliniyor...
                      </span>
                    ) : (
                      'Evet, Hesabımı Sil'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Email Değiştirme Modal */}
        {showEmailModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-8 animate-fade-in">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <FaEnvelope className="text-blue-600 dark:text-blue-400 text-2xl" />
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Email Adresi Değiştir
                  </h2>
                </div>
                <button
                  onClick={() => {
                    setShowEmailModal(false);
                    setNewEmail('');
                    setEmailCode('');
                    setEmailCodeSent(false);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleUpdateEmail} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Mevcut Email
                  </label>
                  <input
                    type="email"
                    value={user?.email || ''}
                    disabled
                    className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-lg border border-gray-300 dark:border-gray-600"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Yeni Email Adresi
                  </label>
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    disabled={emailCodeSent || isUpdatingEmail}
                    placeholder="yeni@email.com"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    required
                  />
                </div>

                {emailCodeSent && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Doğrulama Kodu
                    </label>
                    <input
                      type="text"
                      value={emailCode}
                      onChange={(e) => setEmailCode(e.target.value)}
                      disabled={isUpdatingEmail}
                      placeholder="6 haneli kod"
                      maxLength={6}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      required
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Yeni email adresinize gönderilen 6 haneli kodu girin
                    </p>
                  </div>
                )}

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEmailModal(false);
                      setNewEmail('');
                      setEmailCode('');
                      setEmailCodeSent(false);
                    }}
                    disabled={isUpdatingEmail}
                    className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-white font-semibold rounded-lg transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    disabled={isUpdatingEmail || !newEmail.trim()}
                    className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
                  >
                    {isUpdatingEmail ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        {emailCodeSent ? 'Doğrulanıyor...' : 'Kod Gönderiliyor...'}
                      </span>
                    ) : (
                      emailCodeSent ? 'Güncelle' : 'Kod Gönder'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Şifre Değiştirme Modal */}
        {showPasswordModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-8 animate-fade-in">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <FaKey className="text-purple-600 dark:text-purple-400 text-2xl" />
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Şifre Değiştir
                  </h2>
                </div>
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleUpdatePassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <FaLock className="inline mr-1" />
                    Mevcut Şifre
                  </label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    disabled={isUpdatingPassword}
                    placeholder="Mevcut şifrenizi girin"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Yeni Şifre
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={isUpdatingPassword}
                    placeholder="En az 6 karakter"
                    minLength={6}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Yeni Şifre (Tekrar)
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={isUpdatingPassword}
                    placeholder="Yeni şifrenizi tekrar girin"
                    minLength={6}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    required
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordModal(false);
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                    }}
                    disabled={isUpdatingPassword}
                    className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-white font-semibold rounded-lg transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    disabled={isUpdatingPassword || !currentPassword || !newPassword || !confirmPassword}
                    className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
                  >
                    {isUpdatingPassword ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Güncelleniyor...
                      </span>
                    ) : (
                      'Güncelle'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Crop Modal */}
        {showCropModal && imageSrc && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Fotoğrafı Kırp
                </h2>
                <button
                  onClick={() => {
                    setShowCropModal(false);
                    setImageSrc(null);
                    setCrop({ x: 0, y: 0 });
                    setZoom(1);
                    setCroppedAreaPixels(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  disabled={isUploadingImage}
                >
                  <FaTimes className="w-6 h-6" />
                </button>
              </div>

              <div className="relative w-full" style={{ height: '400px', background: '#333' }}>
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1} // Kare (1:1)
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  cropShape="round" // Yuvarlak crop
                  showGrid={false}
                />
              </div>

              {/* Zoom Control */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Yakınlaştır: {Math.round(zoom * 100)}%
                </label>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(e) => setZoom(parseFloat(e.target.value))}
                  className="w-full"
                  disabled={isUploadingImage}
                />
              </div>

              {/* Buttons */}
              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowCropModal(false);
                    setImageSrc(null);
                    setCrop({ x: 0, y: 0 });
                    setZoom(1);
                    setCroppedAreaPixels(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  disabled={isUploadingImage}
                  className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-white font-semibold rounded-lg transition-colors flex items-center justify-center space-x-2"
                >
                  <FaTimes />
                  <span>İptal</span>
                </button>
                <button
                  onClick={createCroppedImage}
                  disabled={isUploadingImage || !croppedAreaPixels}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center justify-center space-x-2"
                >
                  {isUploadingImage ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span>Yükleniyor...</span>
                    </>
                  ) : (
                    <>
                      <FaCheck />
                      <span>Onayla ve Yükle</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
